/*
# -----------------------------------------------------------------------------
# Component: creditNoteAjaxMixin.js
# Author: antonio.ramirez@pst.sg
# Created Date: 12/May/2019
# Comments: Responsible for all backend related calls for Manage CreditNote
# -----------------------------------------------------------------------------
*/
import MessageMixin from '@/mixins/message'
import { expandURI, filterQuery } from '@/utils/ajax'
import { formatDisplayDate, formatDisplayDateTime2, formatDisplayDateTime4, removeTime } from '@/utils/date'
import clone from 'clone'
import { formatPrice } from '@/utils/index'
import { CREDITNOTE_LOAD_DATA, CHG_ACCT_MCH_RECKEY } from '@/store/types'
import { recordLockMixin } from '@/mixins/common/recordLockMixin'
import { accountsAjaxCalls } from '@/mixins/accounting/accountsAjaxCalls'
import { strJoin, checkNull } from '@/utils/stringutil'

export const creditNoteAjaxMixin = {
  data() {
    return {
      actionName: ''
    }
  },
  created() {
    // AR: Initialize CommonUtil Menu 29-Jan-2021
    this.initCommonUtilMenu()
  },
  methods: {
    performSearchFilters() {
      let massageFilters = clone(this.creditNoteForm)
      if (massageFilters.transactionDateFrom !== null && massageFilters.transactionDateFrom !== '') {
        massageFilters.transactionDateFrom = formatDisplayDateTime2(removeTime(massageFilters.transactionDateFrom))
      }
      if (massageFilters.transactionDateTo !== null && massageFilters.transactionDateTo !== '') {
        massageFilters.transactionDateTo = formatDisplayDateTime2(removeTime(massageFilters.transactionDateTo))
      }
      return massageFilters
    },
    async performSearch() {
      const apiUrl = this.$store.state.creditNote.menu.performSearch
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.pagination
          const paramMap = {
            page: page - 1,
            size: itemsPerPage
          }

          const result = await this.$http.get(filterQuery(expandURI(apiUrl, paramMap), this.performSearchFilters(), this.pagination.sortBy, this.pagination.sortDesc))
          const { data } = result.data
          if (data) {
            this.totalItems = result.data.totalElements
            this.items = []
            data.map((result) => {
              const resultList = {
                accountNo: result.acctNo,
                branch: result.branch,
                currency: result.curr,
                documentNo: result.docNo,
                tranDate: this.formatDate(result.tranDate),
                amount: formatPrice(result.debitAmt),
                gst: formatPrice(result.othGST),
                netAmt: formatPrice(result.netAmount),
                locNetAmt: formatPrice(result.locAmt),
                suffix: result.suffix,
                fund: result.fund,
                type: result.type
              }
              this.items.push(resultList)
            })
          } else {
            this.items = []
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        } finally {
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
        this.loading = false
      }
    },
    performSaveLockRecord(recKey) {
      if (this.creditNoteForm.actionName === 'CREATE_MATCH_NOW') {
        this.$store.commit(CHG_ACCT_MCH_RECKEY, recKey)
        this.checkRecordLock(recKey, 'ACT')
      }
    },
    performSaveUnlockRecord(recKey, docNo) {
      if (!(this.creditNoteForm.actionName === 'CREATE_SAVE_POST' ||
        this.creditNoteForm.actionName === 'CREATE_SAVE_REVIEW' ||
        this.creditNoteForm.actionName === 'CREATE_MATCH_NOW' ||
        this.creditNoteForm.actionName === 'UPDATE_MATCH_NOW') &&
        (docNo !== '' && docNo !== undefined && docNo !== null)) {
        // AR: OS-660 Unlock Record upon exit 29-Jan-2021
        this.unlockRecord(recKey, 'ACT')
        this.$store.commit(CHG_ACCT_MCH_RECKEY, '')
      }
    },
    performSaveNavigate() {
      if (this.creditNoteForm.actionName === 'CREATE_SAVE_REVIEW' || this.creditNoteForm.actionName === 'UPDATE_SAVE_REVIEW') {
        this.$router.push({ name: 'creditNote' })
      } else {
        this.$router.push({ name: 'glCreditNote' })
      }
    },
    performSaveFilters() {
      let massageFilters = clone(this.creditNoteForm)
      if (massageFilters.transactionDate !== '') {
        massageFilters.transactionDate = formatDisplayDate(massageFilters.transactionDate)
      }
      return massageFilters
    },
    async performSave() {
      const apiURI = this.$store.state.creditNote.menu.performAction
      if (apiURI) {
        try {
          const result = await this.$http.post(apiURI, this.performSaveFilters())
          const { data } = result
          if (data) {
            const creaditNoteInfo = {
              tranMode: 'R',
              branch: this.creditNoteForm.branch,
              accountNo: this.creditNoteForm.accountNo,
              type: this.creditNoteForm.transactionType,
              documentNo: data.resultMap.documentNo,
              fund: this.creditNoteForm.fund,
              suffix: this.creditNoteForm.accountNoSuffix,
              currency: this.creditNoteForm.netAmountCurr,
              amount: this.creditNoteForm.amount,
              gstAE: this.creditNoteForm.gstAmountAE,
              gst: this.creditNoteForm.gstAmount,
              netAmt: this.creditNoteForm.netAmount,
              netLocAmtAE: this.creditNoteForm.netAmountLocalAE,
              netLocAmt: this.creditNoteForm.netLocalAmount,
              tranDate: this.creditNoteForm.transactionDate,
              actionName: this.creditNoteForm.actionName,
              printDoc: this.creditNoteForm.printDocument
            }
            this.$store.commit(CREDITNOTE_LOAD_DATA, creaditNoteInfo)

            let docNo = this.getDocumentNo(this.creditNoteForm.documentNo, data.resultMap.documentNo, this.creditNoteForm.actionName)
            this.creditNoteForm.documentNo = data.resultMap.documentNo

            let recKey = strJoin('|', this.creditNoteForm.transactionType, this.creditNoteForm.branch,
              this.creditNoteForm.accountNo, docNo, this.creditNoteForm.fund, this.creditNoteForm.accountNoSuffix)

            this.performSaveLockRecord(recKey)

            this.notify(data)
            this.performSaveUnlockRecord(recKey, docNo)
            this.performSaveNavigate()
            this.doExit()
          }
        } catch (err) {
          const { data } = err.response
          this.showPromptLeave = true
          if (data) {
            this.processingFlag = false
            this.message(data)
            this.fieldMessage(data)
          }
        }
      } else {
        this.showPromptLeave = true
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async performReview() {
      const apiURI = this.$store.state.creditNote.menu.performAction
      if (apiURI) {
        try {
          this.creditNoteForm.actionName = 'CREDITNOTE_REVIEW'
          this.creditNoteForm.branch = this.creditNoteData.branch
          this.creditNoteForm.accountNo = this.creditNoteData.accountNo
          this.creditNoteForm.transactionType = this.creditNoteData.type
          this.creditNoteForm.documentNo = this.creditNoteData.documentNo
          this.creditNoteForm.fund = this.creditNoteData.fund
          this.creditNoteForm.accountNoSuffix = this.creditNoteData.suffix
          const response = await this.$http.post(apiURI, this.creditNoteForm)
          const { data } = response.data
          if (data) {
            this.$set(this, 'creditNoteForm', data.accounts)
            this.creditNoteForm.amount = formatPrice(data.accounts.amount)
            this.creditNoteForm.gstAmount = formatPrice(data.accounts.gstAmount)
            this.creditNoteForm.netAmount = formatPrice(data.accounts.netAmount)
            this.creditNoteForm.netLocalAmount = formatPrice(data.accounts.netLocalAmount)
            this.creditNoteForm.docAE = data.accounts.docAE === 'true'
            this.creditNoteForm.gstAmountAE = data.accounts.gstAmountAE === 'true'
            this.creditNoteForm.netAmountLocalAE = data.accounts.netAmountLocalAE === 'true'
            this.creditNoteForm.oldFund = checkNull(data.accounts.fund)
            this.creditNoteForm.oldAccountNoSuffix = checkNull(data.accounts.accountNoSuffix)
            this.creditNoteForm.oldAccountNo = checkNull(data.accounts.accountNo)
            this.creditNoteForm.policyNo = ''
            if (data.accounts.polNo !== '' && data.accounts.polNo !== null) {
              this.creditNoteForm.policyNo = data.accounts.polNo + '-' + data.accounts.renCnt + '-' + data.accounts.endtCnt
            }
            this.fetchUserDetails(data.accounts.createdBy).then(result => {
              this.creditNoteForm.createdBy = result
            })
            this.creditNoteForm.createdDate = formatDisplayDateTime4(data.accounts.createdDate)
            this.fetchUserDetails(data.accounts.modifiedBy).then(result => {
              this.creditNoteForm.modifiedBy = result
            })
            this.creditNoteForm.modifiedDate = formatDisplayDateTime4(data.accounts.modifiedDate)
          }
        } catch (err) {
          const { data } = err.response
          this.showPromptLeave = true
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      } else {
        this.showPromptLeave = true
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchBranch() {
      this.branches = []
      let apiUrl = this.$store.state.creditNote.menu.fetchBranch
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.branches.push(data)
              })
            }
          }
        })
      }
    },
    async fetchDepartment() {
      this.departments = []
      let apiUrl = this.$store.state.creditNote.menu.fetchDepartment
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.departments.push(data)
              })
            }
          }
        })
      }
    },
    async fetchFund() {
      this.funds = []
      let apiUrl = this.$store.state.creditNote.menu.fetchFund
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.funds.push(data)
              })
            }
          }
        })
      }
    },
    async fetchAmountCurrency() {
      this.amountcurrencies = []
      let apiUrl = this.$store.state.creditNote.menu.fetchAmountCurrency
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.amountcurrencies.push(data)
              })
            }
          }
        })
      }
    },
    async fetchAccount() {
      this.accounts = []
      let apiUrl = this.$store.state.creditNote.menu.fetchAccount
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.accounts.push(data)
              })
            }
          }
        })
      }
    },
    async fetchAcctNoSfx60() {
      this.acctnosuffixes60 = []
      let apiUrl = this.$store.state.creditNote.menu.fetchAcctNoSfx60
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.acctnosuffixes60.push(data)
              })
            }
          }
        })
      }
    },
    async fetchGstStatus() {
      this.gstStatuses = []
      let apiUrl = this.$store.state.creditNote.menu.fetchGstStatus
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.gstStatuses.push(data)
              })
            }
          }
        })
      }
    },
    async fetchCurrency() {
      this.currencies = []
      let apiUrl = this.$store.state.creditNote.menu.fetchCurrency
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.currencies.push(data)
              })
            }
          }
        })
      }
    },
    async fetchBranchWBranchUserId() {
      this.branches = []
      let apiUrl = this.$store.state.creditNote.menu.fetchBranchWBranchUserId
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.branches.push(data)
              })
            }
          }
        })
      }
    },
    async fetchUserDetails(userid) {
      let username = '-'
      let apiUrl = this.$store.state.creditNote.menu.fetchUserDetails
      if (apiUrl) {
        const expand = {
          userId: userid
        }
        await this.$http.get(expandURI(apiUrl, expand)).then(result => {
          if (result.data) {
            const data = result.data.dataMap
            const { userDetails } = data
            username = userDetails.firstName
            if (userDetails.lastName !== null || userDetails.lastName !== '') {
              username = username + ' ' + userDetails.lastName
            }
          }
        })
      }
      return username
    },
    checkFiltersNullValue() {
      const ignore = ['transactionType', 'fund', 'transactionDate', 'actionName', 'applyFilters']
      return Object.keys(this.creditNoteForm)
        .filter(key => !ignore.includes(key))
        .every(key => this.creditNoteForm[key] === null ||
          this.creditNoteForm[key] === '' ||
          this.creditNoteForm[key] === undefined
        )
    },
    doExportFilters() {
      const checknull = this.checkFiltersNullValue()
      if (checknull) {
        this.exportList.promptFilterWarning = true
      } else {
        this.exportList.promptFilterWarning = false
        this.doExportList()
      }
    },
    async doExportList() {
      let columns = []
      let columnsTitle = []
      this.creditNoteColumns.map(t => {
        columnsTitle.push(t.text)
        columns.push(t.value)
      })
      const apiUrl = this.$store.state.creditNote.menu.exportAcctTypeFilteredList
      if (apiUrl) {
        try {
          let massageForm = clone(this.creditNoteForm)
          massageForm.acctType = 'Credit Note'
          massageForm.columns = columns.join(',')
          massageForm.columnsTitle = columnsTitle.join(',')
          await this.$http.post(apiUrl, massageForm)
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    }
  },
  mixins: [
    MessageMixin,
    recordLockMixin,
    accountsAjaxCalls
  ]
}
